import React, { useState, useRef } from 'react';
import { registerUser } from '../../api/userApi';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import Modal from '../common/Modal';
import languageOptions from '../../config/languageOptions';
import { isPasswordValid } from '../../utils/authenticationUtils';
import './RegisterPage.css';
import HomeButton from '../common/HomeButton';

const RECAPTCHA_SITE_KEY = window.process.env.RECAPTCHA_SITE_KEY;

function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    dateOfBirth: '',
    preferredLanguage: 'en-US', // change to browser language detection
    gender: 'not communicated'
  });
  const [error, setError] = useState('');
  const [showRegisteredModal, setShowRegisteredModal] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const recaptcha = useRef();


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    setError();
    e.preventDefault();

    const captchaValue = recaptcha.current.getValue();

    if (!captchaValue) {
      setError(t('register.completeCaptcha'));
      recaptcha.current?.reset();
      return;
    }

    if (!isPasswordValid(formData.password)) {
      setError(t('register.errorPasswordInvalid'));
    }
    try {
      const data = await registerUser({captchaValue, ...formData});
      if (data.success) {
        setShowRegisteredModal(true);
      }
    } catch (error) {
      if (error.response && error.response.status === 429) {
        setError(t('register.tooManyRequests'))
      } else if (error.response && error.response.status === 400) {
        const response = error.response.data;
        if (response.missingFields) {
          const missingFieldsTrans = response.missingFields.map(fieldname => t(`register.${fieldname}`)).join(', ');
          setError(`${t('register.pleaseProvide')} ${missingFieldsTrans}`);
        } else if (response.message === 'password is not valid') {
          setError(t('register.errorPasswordInvalid'));  
        } else if (response.message === 'Invalid language code') {
          setError(t('register.errorLanguageCode'));  
        } else if (response.message === 'User already exists') {
          setError(t('register.errorExistingEmail'));  
        } else if (response.message === 'CAPTCHA verification failed') {
          setError(t('register.completeCaptcha'));  
        } else if (response.errors) {
          setError(response.errors.map(err => `${err.field}: ${err.message}`).join(', '));
        } else {
          setError(t('register.registrationFailed'));
        }
      } else {
        console.error('Registration error', error);
        setError(t('register.registrationFailed'));
      }
      recaptcha.current?.reset();
    }
  };


  return (
    <div className="register-page">
      <HomeButton />
      <form className='register-form' onSubmit={handleSubmit}>

        <div>
          <input type="email" name="email" placeholder={t('register.email')} value={formData.email} onChange={handleChange} required />
        </div>
        <div>
          <input type="password" name="password" placeholder={t('register.password')} value={formData.password} onChange={handleChange} required />
        </div>
        <div>
          <input type="text" name="name" placeholder={t('register.name')} value={formData.name} onChange={handleChange} required />
        </div>
        <div className='label-field-container'>
          <span>{t('register.dob')}</span>
          <input type="date" name="dateOfBirth" placeholder={t('register.dob')} value={formData.dateOfBirth} onChange={handleChange} />
        </div>
        <div className='label-field-container'>
          <span>{t('register.gender')}</span>
          <select name="gender" value={formData.gender} onChange={handleChange}>
            <option value="boy">{t('register.boy')}</option>
            <option value="girl">{t('register.girl')}</option>
            <option value="not communicated">{t('register.nc')}</option>
          </select>
        </div>
        <div className='label-field-container'>
          <span>{t('register.preferredLanguage')}</span>
          <select
              name="preferredLanguage"
              value={formData.preferredLanguage}
              onChange={handleChange}
          >
              {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                      {option.label}
                  </option>
              ))}
          </select>
        </div>
        <div className='captcha'>
          <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              ref={recaptcha}
              theme='dark'
          />
        </div>
        {error && <p className='form-error'>{error}</p>}
        <button className='big-button action-button' type="submit">
          <i className="fa-solid fa-user-check"></i>
          <span>{t('register.register')}</span>
        </button>
      </form>
      <h2 className='register-h2'>{t('register.alreadyRegistered')} <button className='link-button' onClick={() => navigate('/login')}>{t('register.loginHere')}</button></h2>
      <Modal isOpen={showRegisteredModal} onClose={() => navigate('/login')}>
        <h2>{t('register.thankYou')}</h2>
        <p>{t('register.confirmationEmailSent')}</p>
        <div className="modal-buttons">
            <button className="action-btn" onClick={() => navigate('/login')}>{t('register.login')}</button>
        </div>
      </Modal>
    </div>
  );
}

export default RegisterPage;
